import React, {useEffect, useState} from 'react';
import {Button, Modal, Select, Space, Upload} from 'antd';
import useHTTP from '@/hooks/useHTTP.mjs';
import runAsync from '@/script/runAsync.mjs';
import Table from '@/biz/Table.jsx';
import ArType from '@/components/SoftwareMap/nodes/AR/ArType.jsx'
import usePermission from "../DpList/usePerms.mjs";
import FileUtils from '@/script/FileUtils';

/**
 * 错误节点替换DLG
 * @param param
 *  modules: [{moduleType, moduleId, moduleName}]
 * @param onOk
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const Pane = ({prjId, sVer, dpId, dpBizNodeType, onOk, ...props}) => {
    const http = useHTTP();

    // 数据
    const [rows, setRows] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [fileList, setFileList] = useState([]);

    // 加载显示
    const [loading, setLoading] = useState(false);

    const arTypeOptions = ArType.flatMap(([_, ...items]) => items.map(([value, label]) => ({label, value})));


    // 操作权限
    const hasUpt = usePermission([dpBizNodeType + '_UPT'], [prjId + sVer], []);


    // 组件初始化
    useEffect(() => {
        (async () => {
            if (dpBizNodeType) {
                init()
            }
        })()
    }, [dpBizNodeType]);

    const init = async () => {
        try {
            setLoading(true)
            const data = await runAsync(
                async () => {
                    return await http.get(`/errNodeReplaceConfig/listErrNodeReplaceConfig`, {dpBizNodeType});
                }, {
                    error: {content: (err) => `查询失败: ${err.message}`},
                    loading: {show: false},
                    success: {show: false},
                });

            if (data) {
                setRows(data)
            }
        } finally {
            setLoading(false)
        }
    }

    const rowSelection = {
        selectedRowKeys: selectedRows.map(({pkid}) => pkid),
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRows(selectedRows);
        },
    }

    // 去重数组
    const uniqueByFieldKeepLast = (array, field) => {
        const map = new Map();
        array.forEach(item => map.set(item[field], item));
        return Array.from(map.values());
    }

    // 确认
    const doConfirm = () => {
        Modal.confirm({
            title: '操作提示',
            okText: '确认',
            cancelText: '取消',
            content: '是否确认补全？',
            okType: 'danger',
            onOk: () => {
                doSave();
            }
        });
    }

    // 容错性补全
    const doSave = async () => {
        if (rows.length > 0) {
            for (let i = 0; i < rows.length; i++) {
                const {errNodeTitle, arType} = rows[i];
                if (!errNodeTitle) {
                    Modal.error({title: '提示信息', content: '错误节点标题不能为空！'});
                    return;
                }
                if (!arType) {
                    Modal.error({title: '提示信息', content: '替换节点类型不能为空！'});
                    return;
                }
            }

            // 错误节点标题不能重复
            const distinctRows = uniqueByFieldKeepLast(rows, 'errNodeTitle');
            if (distinctRows.length != rows.length) {
                Modal.error({title: '提示信息', content: '请检查错误节点标题是否有重复！'});
                return;
            }
        } else {
            Modal.error({title: '提示信息', content: '请至少添加一条记录！'});
            return;
        }

        try {
            setLoading(true);

            const data = await runAsync(
                async () => {
                    return await http.post(`/dpRefComplete/replaceErrNode${dpBizNodeType}`, {
                        prjId,
                        sVer,
                        dpId,
                        errNodeReplaceConfigList: [...rows]
                    });
                }, {
                    error: {content: (err) => `补全失败: ${err.message}`},
                    loading: {show: false},
                    success: {show: true},
                });

            onOk(data);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    // 导入
    const doImport = async (selFile) => {
        try {
            if (!selFile?.file) {
                return;
            }
            const header = {headers: {'Content-Type': 'multipart/form-data'}};
            const data = await runAsync(
                async () => {
                    return await http.post(`/dpRefComplete/importErrNodeReplaceConfig`, {
                        prjId,
                        sVer,
                        dpId,
                        dpBizNodeType,
                        files: [selFile?.file]
                    }, header);
                }, {
                    error: {content: (err) => `导入失败: ${err.message}`},
                    loading: {show: true},
                    success: {show: true},
                });

            setRows(data)
        } catch (err) {
            console.log(err);
        }
    }

    // 导出
    const doExport = async () => {
        if (rows.length > 0) {
            for (let i = 0; i < rows.length; i++) {
                const {errNodeTitle, arType} = rows[i];
                if (!errNodeTitle) {
                    Modal.error({title: '提示信息', content: '错误节点标题不能为空！'});
                    return;
                }
                if (!arType) {
                    Modal.error({title: '提示信息', content: '替换节点类型不能为空！'});
                    return;
                }
            }

            // 错误节点标题不能重复
            const distinctRows = uniqueByFieldKeepLast(rows, 'errNodeTitle');
            if (distinctRows.length != rows.length) {
                Modal.error({title: '提示信息', content: '请检查错误节点标题是否有重复！'});
                return;
            }
        } else {
            Modal.error({title: '提示信息', content: '请至少添加一条记录！'});
            return;
        }

        try {
            const data = await runAsync(
                async () => {
                    return await http.post(`/dpRefComplete/exportErrNodeReplaceConfig`, {
                        prjId,
                        sVer,
                        dpId,
                        dpBizNodeType,
                        errNodeReplaceConfigList: [...rows]
                    });
                }, {
                    error: {content: (err) => `导出失败: ${err.message}`},
                    loading: {show: true},
                    success: {show: true},
                });

            FileUtils.download(data.blob, data.fileName);
        } catch (err) {
            console.log(err);
        }
    }

    // 选择变更
    const onInputChange = (e, record, key) => {
        const newRows = [...rows];
        const index = newRows.findIndex(r => r.pkid == record.pkid);
        newRows[index] = {...newRows[index], [key]: (e && e.target ? e.target.value : e)};
        setRows(newRows)
    }

    const columns = [
        {
            title: '错误节点标题',
            dataIndex: 'errNodeTitle',
            width: 300,
            ellipsis: true,
            fixed: 'left',
            filter: <Table.FilterText/>,
            render: (text, record, index) => (
                <Table.InputLongText value={text} size="small"
                                     onChange={(e) => onInputChange(e, record, 'errNodeTitle')}/>
            )
        },
        {
            title: '替换节点类型',
            dataIndex: 'arType',
            width: 200,
            ellipsis: true,
            getValue: row => (
                arTypeOptions?.find(item => item.value == row.arType)?.label ?? row.arType
            ),
            filter: <Table.FilterText/>,
            render: (text, record, index) => {
                return <span onClick={(e) => {
                    e.stopPropagation()
                }}>
                     <Select size='small' value={record.arType} style={{width: '100%', border: 'node'}}
                             options={arTypeOptions}
                             showSearch
                             optionFilterProp="label"
                             onChange={(e) => {
                                 onInputChange(e, record, 'arType')
                             }}
                     >
                        </Select>
                </span>
            }
        },
        {
            title: '替换节点标题',
            dataIndex: 'arName',
            width: 300,
            ellipsis: true,
            filter: <Table.FilterText/>,
            render: (text, record, index) => (
                <Table.InputLongText value={text} size="small"
                                     onChange={(e) => onInputChange(e, record, 'arName')}/>
            )
        },
    ];

    const doAdd = () => {
        const module = {
            pkid: crypto.randomUUID(),
        }
        rows.push(module);
        setRows([...rows]);
    }

    const doRemove = () => {
        const filterArrays = selectedRows.map(({pkid}) => pkid)
        setRows(rows.filter(({pkid}) => !filterArrays.includes(pkid)))
        setSelectedRows([])
    }

    return (
        <>
            <Modal
                title={'容错性补全'}
                okText="确定"
                cancelText="取消"
                width={1200}
                bodyStyle={{minHeight: '250px', maxHeight: '60vh', overflowY: 'auto'}}
                {...props}
                footer={[
                    hasUpt && <Space>
                        <Button onClick={() => onOk()}>取消</Button>
                        <Upload
                            accept=".xls,.xlsx"
                            fileList={fileList}
                            multiple={false}
                            showUploadList={false}
                            customRequest={doImport}
                            beforeUpload={(file, files) => {
                                setFileList(files);
                                return true;
                            }}
                            onRemove={(file) => {
                                if (file) {
                                    const notDeletes = fileList.filter(f => f.name != file.name);
                                    setFileList([...notDeletes]);
                                }
                            }}
                        >
                            <Button onClick={() => doImport()} loading={loading}>导入</Button>
                        </Upload>
                        <Button onClick={() => doExport()} loading={loading}>导出</Button>
                        <Button type="primary" onClick={() => doConfirm()} loading={loading}>确定</Button>
                    </Space>
                ]}
            >


                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 12,}}>
                    <Space>
                        <Button type="primary" onClick={() => doAdd()}>新增</Button>
                        <Button type="danger" onClick={() => doRemove()}>删除</Button>
                    </Space>
                </div>
                <Table
                    loading={loading}
                    size="middle"
                    columns={columns}
                    dataSource={rows}
                    noRowNumber={true}
                    rowKey={"pkid"}
                    rowSelection={{type: "checkbox", ...rowSelection}}
                />
            </Modal>
        </>
    );
}

export default Pane;
